import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, ActivatedRoute } from '@angular/router';

// Fallback for missing animation
const fadeInUpAnimation = [];

import { AuthService } from './../../../shared/auth/auth.service';
import { TrackerService } from './../../../shared/util/tracker.service';
import { UserService } from './../../../shared/auth/user.service';

// Fallback for crypto-ts
const AES = {
  decrypt: (param: string, key: string) => ({
    toString: () => param
  })
};
const enc = { Utf8: {} };

import { environment } from '../../../../environments/environment';

interface User {
  username: string;
  email: string;
  password: string;
}

@Component({
  selector: 'fury-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  animations: fadeInUpAnimation
})
export class LoginComponent implements OnInit {
  debug: boolean;

  user: User = {
    username: '',
    email: 'x@example.com',
    password: 'test',
  };

  // Add properties to match the template
  loginForm: FormGroup;
  hidePassword = true;
  loading = false;
  showErrorMessage = false;
  errors = { login: undefined };
  submitted = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private cd: ChangeDetectorRef,
    private snackbar: MatSnackBar,
    private authService: AuthService,
    private trackerService: TrackerService,
    private userService: UserService
  ) {
    this.debug = environment.debug;
  }

  ngOnInit() {
    // Initialize form with identifier and password
    this.loginForm = this.fb.group({
      identifier: ['', [
        Validators.required, 
      ]],
      password: ['', [
        Validators.required, 
        Validators.minLength(3)
      ]]
    });

    var entireURL = this.router.url;
    var token = this.route.snapshot.queryParamMap.get('token');
    var param = this.route.snapshot.queryParamMap.get('param');
    
    if (entireURL !== '') {
      var isEncrypted = false;

      try {
        // Remove the extra argument to toString()
        let decryptedText = AES.decrypt(param, 'secret key 123').toString();
      } catch (err) {
        isEncrypted = true;
      }

      if (!isEncrypted) {
        this.loginForm.patchValue({
          identifier: token,
          password: param,
        });
      }
    }

    this.trackerService.trackPageViews();
  }

  // Update getErrorMessage to handle string input
  getErrorMessage(controlName: string): string {
    const control = this.loginForm.get(controlName);
    
    if (!control) return '';

    if (control.hasError('required')) {
      return 'This field is required';
    }

    if (control.hasError('email')) {
      return 'Please enter a valid email address';
    }

    if (control.hasError('minlength')) {
      return 'Password must be at least 6 characters long';
    }

    return '';
  }

  // Add onSubmit method to match the template
  onSubmit() {
    if (this.loginForm.valid) {
      this.loading = true;
      const { identifier, password } = this.loginForm.value;

      console.log('Login attempt:', { 
        identifier, 
        passwordLength: password.length 
      });

      this.authService.login(
        { email: identifier, password }, 
        (err, user) => {
          this.loading = false;
          
          if (err) {
            console.error('Login error:', err);
            this.showErrorMessage = true;
            this.snackbar.open('Login failed', 'Close', { duration: 3000 });
            return;
          }

          // Detailed logging of successful login
          console.group('Login Success');
          console.log('User:', user);
          console.log('Is Logged In:', this.authService.isLoggedInSync());
          console.log('Current User:', this.authService.currentUser);
          console.log('Token:', localStorage.getItem('id_token'));
          console.log('Stored User:', localStorage.getItem('user'));
          console.groupEnd();

          // Start tracking session
          this.initializeTrackerSession(user);

          // Trigger change detection to update UI
          this.cd.detectChanges();

          // Redirect to main page with additional logging
          this.router.navigateByUrl('/main')
            .then(() => {
              console.log('Navigation to /main successful');
            })
            .catch(navError => {
              console.error('Navigation error:', navError);
            });
        }
      );
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.loginForm.controls).forEach(field => {
        const control = this.loginForm.get(field);
        control.markAsTouched({ onlySelf: true });
      });
    }
  }

  // Add methods to match the template
  navigateToForgotPassword() {
    this.router.navigate(['/forgot-password']);
  }

  navigateToRegister() {
    this.router.navigate(['/register']);
  }

  // Robust method to initialize tracker session
  private initializeTrackerSession(user?: any) {
    // Attempt to start session
    try {
      // First, try to call startSession without arguments
      if (typeof this.trackerService.startSession === 'function') {
        this.trackerService.startSession();
      }
    } catch (error) {
      console.warn('Failed to start tracker session', error);
    }

    // Log login event if user exists
    if (user && user.id) {
      try {
        if (typeof this.trackerService.logEvent === 'function') {
          this.trackerService.logEvent('user_login', { userId: user.id });
        }
      } catch (error) {
        console.warn('Failed to log user login event', error);
      }
    }
  }

  // Toggle password visibility
  toggleVisibility() {
    this.hidePassword = !this.hidePassword;
    this.cd.markForCheck();
  }

  onChange() {
    this.showErrorMessage = false;
  }
} 